import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { writeFetch } from '@/api';

export default function EmailCapture({ results, onComplete }) {
  const [formData, setFormData] = useState({ firstName: '', email: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      name: formData.firstName,
      first_name: formData.firstName,
      email: formData.email,
      tag: 'Stewardship Audit',
      christian_score: results.scores.christian,
      creator_score: results.scores.creator,
      caretaker_score: results.scores.caretaker,
      connector_score: results.scores.connector,
      weakest_persona: results.weakest,
      strongest_persona: results.strongest,
      christian_tier: results.tiers.christian,
      creator_tier: results.tiers.creator,
      caretaker_tier: results.tiers.caretaker,
      connector_tier: results.tiers.connector,
      owner_id: 'public_submission'
      // Explicitly removed created_at to let the database generate its own valid timestamp
    };

    try {
      // 1. Save data to NoCodeBackend Database
      console.log("Sending payload to NCB:", payload);
      await writeFetch('create/audit_results_1715000000', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      console.log("✅ Successfully saved to NCB");
    } catch (err) {
      console.error("🚨 Failed to save to NCB Database:", err);
    }

    try {
      // 2. Fire webhook to Encharge (if configured)
      const enchargeUrl = import.meta.env.VITE_ENCHARGE_WEBHOOK_URL;
      if (enchargeUrl) {
        await fetch(enchargeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).catch(err => console.error("Encharge Webhook Error:", err));
      }

      // 3. Fire PHP Mailer natively on NameHero (Using .env variable)
      const mailerUrl = import.meta.env.VITE_MAILER_URL;
      if (mailerUrl) {
        await fetch(mailerUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).catch(err => console.error("PHP Mailer Error:", err));
      } else {
        console.warn("⚠️ VITE_MAILER_URL is missing in .env file.");
      }

    } catch (err) {
      console.error('Submission processing error', err);
    } finally {
      // Always proceed to show results gracefully
      setLoading(false);
      onComplete(formData.firstName);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      className="max-w-md mx-auto text-center space-y-8"
    >
      <h2 className="text-3xl md:text-4xl font-heading font-bold text-brand-blue leading-tight">
        Where should we send your results?
      </h2>
      <p className="font-body text-gray-700 leading-relaxed text-lg">
        Enter your primary email below to reveal your Stewardship Profile right now, and we'll also send a complete breakdown directly to your inbox.
      </p>

      <form onSubmit={handleSubmit} className="text-left mt-8 bg-white p-8 md:p-10 rounded-[2rem] shadow-xl shadow-brand-blue/5 border border-brand-blue/10">
        <div className="space-y-6">
          <div>
            <label className="block font-heading text-sm font-bold text-brand-blue mb-2 ml-1">First Name</label>
            <input 
              type="text" 
              required 
              className="w-full px-5 py-4 rounded-2xl border-2 border-brand-beige/50 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-brand-blue/10 focus:border-brand-blue outline-none font-body transition-all text-gray-800 placeholder-gray-400"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              placeholder="Your first name"
            />
          </div>
          <div>
            <label className="block font-heading text-sm font-bold text-brand-blue mb-2 ml-1">Email Address</label>
            <input 
              type="email" 
              required 
              className="w-full px-5 py-4 rounded-2xl border-2 border-brand-beige/50 bg-gray-50/50 focus:bg-white focus:ring-4 focus:ring-brand-blue/10 focus:border-brand-blue outline-none font-body transition-all text-gray-800 placeholder-gray-400"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="you@example.com"
            />
          </div>
        </div>

        <button 
          type="submit" 
          disabled={loading}
          className="w-full bg-brand-orange text-white font-heading font-bold py-4 rounded-2xl shadow-lg shadow-brand-orange/20 hover:bg-opacity-90 hover:-translate-y-0.5 transition-all duration-300 flex justify-center items-center mt-8 text-lg disabled:opacity-70 disabled:hover:translate-y-0"
        >
          {loading ? 'Preparing Results...' : 'Reveal My Results →'}
        </button>

        <p className="text-sm font-body text-gray-500 text-center mt-6">
          No spam. Just your results and occasional GospelWise Author content.
        </p>
      </form>
    </motion.div>
  );
}